import { DrawerScreenProps } from '@react-navigation/drawer';
import { useStoreActions, useStoreState } from '@/stores/store';
import { Platform, useWindowDimensions, View } from 'react-native';
import { Text } from '@/components/Text';
import { SettingsDrawerParamList } from '@/screens/TherapistSettings';
import { togglePatternLockOption } from '@//api/services/user';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { Toggle } from '@/components/ui/therapist/toggle';
import Card from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';

import { styles } from './styles';
import colors from '@/config/colors';

export function LockScreen({
  navigation,
}: DrawerScreenProps<
  SettingsDrawerParamList & TherapistStackParamList,
  'LockScreen'
>) {
  const { width } = useWindowDimensions();
  const isSmallScreenSize =
    (Platform.OS === 'web' && width < 720) ||
    (Platform.OS !== 'web' && width < 400);
  const { appStartResumeLock, eigentrainingLock, appLockPattern } =
    useStoreState((state) => ({
      appStartResumeLock: state.appStartResumeLock,
      eigentrainingLock: state.eigentrainingLock,
      appLockPattern: state.appLockPattern,
    }));
  const { changeOnAppStartResumeLock, changeOnEigentrainingLock } =
    useStoreActions((actions) => ({
      changeOnAppStartResumeLock: actions.changeOnAppStartResumeLock,
      changeOnEigentrainingLock: actions.changeOnEigentrainingLock,
    }));
  const { showNotification } = useNotification();

  async function handleClickCreateLockPattern() {
    navigation.navigate('SetPassword');
  }

  function handleChangeOnAppStart() {
    togglePatternLockOption('appStartResumeLock', !appStartResumeLock)
      .then((res) => {
        changeOnAppStartResumeLock();
      })
      .then(() => {
        showNotification({
          type: 'success',
          title: !appStartResumeLock
            ? 'Sperrmuster aktiviert'
            : 'Sperrmuster deaktiviert',
          message: !appStartResumeLock
            ? 'Einstellung erfolgreich aktualisiert.'
            : '',
        });
      });
  }

  async function handleChangeOnSelfTraining() {
    try {
      const responseStatus = (await togglePatternLockOption(
        'eigentrainingLock',
        !eigentrainingLock,
      )) as number;
      if (responseStatus !== 200) throw new Error('Etwas ist schief gelaufen');
      showNotification({
        type: 'success',
        title: !eigentrainingLock
          ? 'Sperrmuster aktiviert'
          : 'Sperrmuster deaktiviert',
        message: !eigentrainingLock
          ? 'Einstellung erfolgreich aktualisiert'
          : '',
      });
      changeOnEigentrainingLock();
    } catch (error) {
      if (error instanceof Error) {
        showNotification({
          type: 'error',
          title: error.message,
        });
      }
    }
  }

  return (
    <BodyWrapper>
      <View>
        <View style={{ padding: 8 }}>
          <Text style={styles.titleFont}>Sperrbildschirm</Text>
        </View>

        <Card>
          <View>
            <Text style={{ color: colors.newUI.therapist.textBlue100 }}>
              Erstellen Sie ein Sperrmuster und aktivieren Sie den
              Sperrbildschirm, um Ihre Anwendung vor unbefugtem Zugriff zu
              schützen.
            </Text>
          </View>
        </Card>

        <Card styleBody={{ padding: 0 }}>
          <View
            style={[
              styles.lockscreenSection,
              { flexDirection: isSmallScreenSize ? 'column' : 'row' },
            ]}
          >
            <View
              style={[
                { flex: 1 },
                isSmallScreenSize && {
                  alignSelf: 'flex-start',
                  paddingBottom: 8,
                },
              ]}
            >
              <Text style={{ fontWeight: 600 }}>Sperrmuster</Text>
            </View>
            <Button
              style={
                isSmallScreenSize && { width: '100%', justifyContent: 'center' }
              }
              onPress={handleClickCreateLockPattern}
              label={appLockPattern ? 'Ändern' : 'Einrichten'}
              look="secondary"
            />
          </View>
        </Card>

        {!!appLockPattern && (
          <Card
            iconPosition="outside"
            title="Sperrmuster abfragen"
            icon="sliders"
            styleBody={{ padding: 0 }}
          >
            <View style={[styles.lockscreenSettings, { borderTopWidth: 1 }]}>
              <Text>Beim Öffnen der App</Text>
              <Toggle
                value={appLockPattern === '' ? false : appStartResumeLock}
                onToggle={handleChangeOnAppStart}
              />
            </View>
            <View style={styles.lockscreenSettings}>
              <Text>Nach Eigentraining (Klinik-Version)</Text>
              <Toggle
                value={appLockPattern === '' ? false : eigentrainingLock}
                onToggle={handleChangeOnSelfTraining}
              />
            </View>
          </Card>
        )}
      </View>
    </BodyWrapper>
  );
}
